import './styles.scss';

import { formatNumber, generateQueryString, handleError, parseQueryString } from '@/utils';

import AddonFilters from '@/features/Addons/components/AddonFilters';
import AddonTable from '@/features/Addons/components/AddonTable';
import { AddonsTypes } from '@/features/Addons/types';
import { ImportStatusEnum } from '@/models/gen/graphql';
import { LoadingBlur } from '@/components/LoadingSpinner';
import ManifestOverridesModal from '@/components/ManifestOverridesModal';
import ManifestUpload from '@/components/ManifestUpload';
import PageInfo from '@/components/PageInfo';
import { ReactElement } from 'react';
import { createPersistentComponentState } from '@/state';
import { customIndexedDBStorage } from '@/state/storage';
import updateImportsStatus from '@/api/services/imports/updateImportsStatus';
import { useNavigate } from 'react-router-dom';

export const useAddonsState = createPersistentComponentState<AddonsTypes.State>('AddonsState', AddonsTypes.initState, {
  storage: customIndexedDBStorage,
});

const Addons = (): ReactElement => {
  const [{ imports, total, loading, addonUpload, addonOverrides, settings }, setState] = useAddonsState(({ state, setState }) => [
    {
      imports: state.imports,
      total: state.total,
      loading: state.loading,
      addonUpload: state.modals.addonUpload,
      addonOverrides: state.modals.addonOverrides,
      settings: state.settings,
    },
    setState,
  ]);

  const currentImports = formatNumber(imports?.length || 0);
  const totalImports = formatNumber(total || 0);

  const onManifestUploadSuccess = async (uploads, files, emailAddress) => {
    try {
      if (!files?.length || !emailAddress) return;
      await parseFilesAndUpdateStatus(uploads, files, emailAddress);
      const { id } = files?.[0] || {};
      setState((current: any): any => ({ ...current, importing: false }));
      if (files.length !== 1) return;
      setState((current) => ({
        ...current,
        modals: {
          ...current.modals,
          addonUpload: { show: false },
        },
      }));
      navigate(`/manifests/${id}${window.location.search}`);
    } catch (err) {
      handleError(err, { notification: { title: 'Manifest Upload' } });
    }
  };

  const parseFilesAndUpdateStatus = async (_uploads, files, emailAddress) => {
    if (!files?.length) return;
    const ids = [];
    for (let u = 0; u < files.length; u++) {
      const { id } = files[u];
      if (!id) continue;
      ids.push(id);
    }
    await updateImportsStatus(ids, ImportStatusEnum.Uploaded, emailAddress);
  };

  const navigate = useNavigate();
  const handleSubmitManifestOverrideModal = (id: string, keepDriversAndVehicles: boolean) => {
    // always hide the modal first
    setState((current) => ({
      ...current,
      modals: {
        ...current.modals,
        addonOverrides: {
          ...current.modals.addonOverrides,
          show: false,
        },
      },
    }));
    // if user prefers drawer, open it; otherwise navigate
    if (settings?.addonAsDrawer) {
      setState((current) => ({ ...current, expandedId: id }));
    } else {
      navigate(`/manifests/${id}?${generateQueryString({ ...parseQueryString(window.location.search), keepDriversAndVehicles })}`);
    }
  };

  return (
    <>
      <AddonFilters />
      <PageInfo>
        Imports: {currentImports} / {totalImports}
      </PageInfo>
      <LoadingBlur loading={loading} />
      <AddonTable />
      <ManifestUpload
        show={addonUpload?.show}
        onHide={(): void => setState((current: any): any => ({ ...current, modals: { ...current.modals, addonUpload: { show: false } } }))}
        onSuccess={onManifestUploadSuccess}
      />
      <ManifestOverridesModal
        show={addonOverrides?.show}
        onHide={(): void =>
          setState((current: any): any => ({ ...current, modals: { ...current.modals, addonOverrides: { show: false } } }))
        }
        importId={addonOverrides?.importId}
        name={addonOverrides?.name}
        onSubmit={handleSubmitManifestOverrideModal}
      />
    </>
  );
};

export default Addons;
